import { Component, OnInit, ChangeDetectionStrategy, inject, signal, viewChild, input } from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {
  registrationPlatformNameRefusal,
  registrationPlatformSettingsRefusal,
  RegistrationPlatformService,
} from '@app/services/registration-platform.service';
import {ProjectRegistrationPlatformSettings} from '@app/modules/shared/models/project-registration-platform';
import {SbSchemaFormComponent} from '@app/_components/unified/sb-schema-form/sb-schema-form.component';
import {SbSchemaServerErrors} from '@app/_components/unified/sb-schema-form/sb-schema-form.types';
import { RegistrationPlatformLogoComponent } from '../thirdparty-platforms-view/registration-platform-logo.component';
import { SbInputFormComponent } from '../../../_components/unified/sb-form/sb-form.component';
import { SbInputTextComponent } from '../../../_components/unified/sb-input/sb-input-text.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { SbSchemaFormComponent as SbSchemaFormComponent_1 } from '../../../_components/unified/sb-schema-form/sb-schema-form.component';

const FIX_FIELDS = 'Please fix the highlighted fields.';

/** The longest install name the server takes. */
export const SETTINGS_NAME_MAX = 100;

/**
 * Body component for SbModalService.open({bodyComponent, modalData: {projectId, instanceId, activate}}):
 * one install's name and settings, the settings rendered from the form its plugin describes (push
 * included, for a plugin that can push). `activate` saves it as active too (the switch was turned
 * on). `refusal` opens it with an earlier refusal's messages (turning on an install whose stored
 * settings no longer pass). A refused save (422, or a name taken in the project) marks its fields
 * and stays open; a saved one closes with the updated row.
 */
@Component({
  selector: 'app-registration-platform-settings-modal',
  templateUrl: './registration-platform-settings-modal.component.html',
  styleUrls: ['./registration-platform-settings-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RegistrationPlatformLogoComponent, SbInputFormComponent, SbInputTextComponent, ReactiveFormsModule, FormsModule, SbSchemaFormComponent_1]
})
export class RegistrationPlatformSettingsModalComponent implements OnInit {
  private readonly registrationPlatformService = inject(RegistrationPlatformService);
  private readonly toastr = inject(ToastrService);

  readonly projectId = input.required<number>();
  /** The install id. */
  readonly instanceId = input.required<number>();
  readonly activate = input(false);
  /** A refusal to show once the form loads: its headline and the fields it names. */
  readonly refusal = input<(SbSchemaServerErrors & {
    message?: string;
  }) | null>(null);
  readonly closeModal = input<(result?: unknown) => void>(undefined);
  readonly dismissModal = input<(reason?: unknown) => void>(undefined);

  readonly settingsForm = viewChild<SbSchemaFormComponent>('settingsForm');

  readonly settings = signal<ProjectRegistrationPlatformSettings | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal('');
  readonly saving = signal(false);
  /** The refusal's headline; the form's own form-wide messages follow it in the banner. */
  readonly message = signal('');
  formErrors: string[] = [];
  /** The install's name, unique in the project. */
  readonly name = signal('');
  readonly nameError = signal('');

  ngOnInit(): void {
    this.registrationPlatformService.getPlatformSettings(this.projectId(), this.instanceId()).subscribe({
      next: (settings) => {
        this.settings.set(settings);
        this.name.set(settings?.name || settings?.platform?.name || '');
        this.loading.set(false);
        const refusal = this.refusal();
        if (refusal) {
          this.message.set(refusal.message || FIX_FIELDS);
        }
      },
      error: (error) => {
        this.loading.set(false);
        this.loadError.set(error?.error?.message || 'The settings could not be loaded.');
      },
    });
  }

  get banner(): string {
    return [this.message(), ...this.formErrors].filter(Boolean).join(' ');
  }

  get submitLabel(): string {
    return this.activate() && !this.settings()?.active ? 'Save & activate' : 'Save';
  }

  onNameChange(name: string): void {
    this.name.set(name || '');
    this.nameError.set('');
  }

  save(): void {
    const form = this.settingsForm();
    if (!form || !this.settings() || this.saving()) {
      return;
    }
    const name = this.name().trim();
    this.nameError.set(this.checkName(name));
    // Both checked, so every message shows at once.
    const formValid = form.validate();
    if (this.nameError() || !formValid) {
      this.message.set(FIX_FIELDS);
      return;
    }
    this.message.set('');
    this.saving.set(true);
    this.registrationPlatformService.saveProjectPlatform(this.projectId(), this.instanceId(), {
      name,
      active: this.activate() || this.settings().active,
      settings: form.value,
    }).subscribe({
      next: (row) => {
        this.saving.set(false);
        this.toastr.success('Settings saved');
        this.closeModal()?.(row);
      },
      error: (error) => {
        this.saving.set(false);
        const nameRefused = registrationPlatformNameRefusal(error);
        if (nameRefused) {
          // Taken by another install of the project, blank or too long.
          this.nameError.set(nameRefused);
          this.message.set(FIX_FIELDS);
          return;
        }
        const refusal = registrationPlatformSettingsRefusal(error);
        if (refusal) {
          this.message.set(error?.error?.message || FIX_FIELDS);
          form.setServerErrors(refusal);
          return;
        }
        this.toastr.error(error?.error?.message || 'The settings could not be saved.', 'Error');
      },
    });
  }

  cancel(): void {
    this.dismissModal()?.('cancel');
  }

  private checkName(name: string): string {
    if (!name) {
      return 'Enter a name.';
    }
    return name.length > SETTINGS_NAME_MAX ? `Use at most ${SETTINGS_NAME_MAX} characters.` : '';
  }
}
